import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { env, HAS_DEV_JWT, IS_DEV, USING_SHIELD_AUTH } from '@/lib/env'
import { msalInstance } from './msal-instance'
import { loginRequest, visionSilentRequest } from './msal-config'
import { getShieldVisionAccessToken } from './shield-token-source'
import { isLocallySignedOut } from './local-logout'

/**
 * Return a valid Vision-facing access token, or throw.
 *
 * Order of preference:
 *   1. Dev-JWT override (only in dev builds, only if the env var is set) — this
 *      unblocks local work before the pulse-v2 Entra app registration lands.
 *   2. Shield access token when `VITE_AUTH_PROVIDER=shield`.
 *   3. Silent MSAL acquisition against the `api://vision-ui/access` scope.
 *   4. Interactive redirect if silent fails with the standard MSAL "you must
 *      prompt" family.
 */
export async function getVisionAccessToken(): Promise<string> {
  if (HAS_DEV_JWT) return env.VITE_DEV_JWT_OVERRIDE
  if (isLocallySignedOut()) throw new Error('Pulse is locally signed out')
  if (USING_SHIELD_AUTH) return getShieldVisionAccessToken()

  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) {
    // No account yet — kick redirect. The follow-up navigation will land back
    // on the same URL and the ProtectedShell will re-render authenticated.
    await msalInstance.loginRedirect(loginRequest)
    throw new Error('Redirecting to sign-in')
  }

  try {
    const result = await msalInstance.acquireTokenSilent({ ...visionSilentRequest, account })
    return result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...visionSilentRequest, account })
      throw new Error('Redirecting for interactive token acquisition')
    }
    if (IS_DEV) console.error('[auth] silent token acquisition failed', err)
    throw err
  }
}
